"use client";

import { useState } from "react";
import {
  afterPracticePass,
  type PracticePass,
} from "@/components/learn-piece/mastery";

/**
 * How many passes in a row have come out **evenly**, on a run without rhythm.
 *
 * The reading that separates « I found the notes » from « I have them ». It
 * decides which of the two acts the card puts forward and what it says under
 * the headline; it decides nothing else, and in particular it no longer
 * crosses the step into time. See `mastery.ts`, « Who decides *when* ».
 *
 * ## Read during render, not in an effect
 *
 * The pattern the runs in time already use for state derived from a change of
 * subject. An effect would paint one frame of the card built from the
 * *previous* pass before the new reading landed, and on this card that frame
 * is a screen contradicting itself: « Ça vient tout seul » over a pass full
 * of holes.
 *
 * ## What breaks the streak, and what does not
 *
 * A pass with holes takes it back to zero — what is being built is « I can
 * play this, again, and again ». So does a passage **given up on**, which
 * from here looks exactly like the « Encore une fois » that opens the next
 * repetition, and is told apart only by the run's own `abandons` counter.
 *
 * `counts` is how a caller excludes a pass that cannot speak for the unit: an
 * accompanied one, where the application played half of it, or a resumed one,
 * where the player worked three bars of four. Those neither count nor break.
 */
export function useFluentStreak(
  lastPass: PracticePass | null,
  abandons: number,
  counts: boolean,
): number {
  const [fluent, setFluent] = useState(0);
  const [countedPass, setCountedPass] = useState<PracticePass | null>(null);
  const [countedAbandons, setCountedAbandons] = useState(abandons);

  if (abandons !== countedAbandons) {
    setCountedAbandons(abandons);
    setFluent(0);
  }

  if (lastPass !== null && lastPass !== countedPass) {
    setCountedPass(lastPass);

    if (counts) {
      setFluent(afterPracticePass(fluent, lastPass));
    }
  }

  return fluent;
}
